import type { GeneratorOptions, Schema } from '@cli-types/core-engine/schema.js'
import type { PageContentGeneratorResult } from '@cli-types/next-adapter/generators.js'
import { createGeneratedFile } from '@cli-utils/files.js'
import {
  escapeTemplateText,
  pluralize,
  quoteLiteral,
  singularize,
  singularizeLabel,
  toCamelCase,
  toKebabCase,
  toPascalCase
} from '@cli-utils/text.js'
import { resolveDisplayNouns } from '@core-engine/schema/display-nouns.js'
import { flattenFields } from '@core-engine/schema/field-helpers/flattening/flatten-fields.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths/resolve-schema-action-import-path.js'
import { buildEntityDevModeIntegration } from '@next-adapter/generators/dev-mode-integration/build-entity-dev-mode-integration.js'
import { resolveEntityFilters } from '@next-adapter/generators/entity-filters/resolve-entity-filters.js'
import { renderPageContentFile } from '@next-adapter/templates/renderers/generators/page-content/generate-page-content.js'
import { formatPropertyAccess } from './format-property-access.js'
import { formatSelectOptions } from './format-select-options.js'

export function generatePageContent(
  schema: Schema,
  pagesDir: string,
  options: GeneratorOptions = {}
): PageContentGeneratorResult {
  const actionImportPath = resolveSchemaActionImportPath(schema.name, options.outputNamespace)
  const singular = singularize(schema.name)
  const plural = pluralize(schema.name)
  const displayNouns = resolveDisplayNouns(schema)
  const Singular = toPascalCase(singular)
  const Plural = toPascalCase(plural)
  const camelPlural = toCamelCase(plural)
  const fileName = `${toKebabCase(plural)}-page-content.tsx`
  const adminRoutePath = options.adminRoutePath ?? '/admin'
  const devModeIntegration = buildEntityDevModeIntegration(
    schema,
    options.adminDir ?? 'admin',
    actionImportPath,
    options.adminImportAlias
  )
  const hasCreate = schema.actions?.create ?? false
  const hasDelete = schema.actions?.delete ?? false
  const resolvedFilters = resolveEntityFilters(schema).map((filter) => ({
    ...filter,
    variableName: toCamelCase(filter.key),
    pascalName: toPascalCase(filter.key),
    fromVariableName: `${toCamelCase(filter.key)}From`,
    toVariableName: `${toCamelCase(filter.key)}To`
  }))
  const hasFilters = resolvedFilters.length > 0
  const distinctFilters = resolvedFilters.filter((filter) => filter.source === 'distinct')

  const flatFields = flattenFields(schema.fields || [])
  const fieldNames = new Set(flatFields.map((f) => f.name))
  const labelParts: string[] = []
  if (fieldNames.has('title')) labelParts.push('item.title as string')
  if (fieldNames.has('name')) labelParts.push('item.name as string')
  labelParts.push('String(item.id)')
  const labelExpr =
    labelParts.length === 1
      ? labelParts[0]
      : `(${labelParts.slice(0, -1).join(' || ')} || ${labelParts[labelParts.length - 1]})`

  const lucideIcons: string[] = ['GripVertical']

  const hookImportNames: string[] = []
  if (distinctFilters.length > 0) {
    for (const filter of distinctFilters) {
      hookImportNames.push(`use${Plural}Distinct${filter.pascalName}`)
    }
  }

  const resolvedFiltersDate = resolvedFilters
    .flatMap((filter) =>
      filter.type === 'date-range'
        ? [
            `    '${filter.key}From': parseAsString.withDefault('')`,
            `    '${filter.key}To': parseAsString.withDefault('')`
          ]
        : [`    '${filter.key}': parseAsString.withDefault('')`]
    )
    .join(',\n')

  const resolvedFiltersDateDate = resolvedFilters
    .flatMap((filter) =>
      filter.type === 'date-range'
        ? [
            `  const ${filter.fromVariableName} = ${formatPropertyAccess('filterValues', `${filter.key}From`)}`,
            `  const ${filter.toVariableName} = ${formatPropertyAccess('filterValues', `${filter.key}To`)}`
          ]
        : [`  const ${filter.variableName} = ${formatPropertyAccess('filterValues', filter.key)}`]
    )
    .join('\n')

  const resolvedFiltersDistinct = resolvedFilters
    .map((filter) =>
      filter.source === 'distinct'
        ? `  const { data: ${filter.variableName}Options } = use${Plural}Distinct${filter.pascalName}()`
        : ''
    )
    .filter(Boolean)
    .join('\n')

  const filterOptions: Array<
    { kind: 'distinct'; filterVariableName: string } | { kind: 'static'; content: string }
  > = resolvedFilters.flatMap(
    (
      filter
    ): Array<
      { kind: 'distinct'; filterVariableName: string } | { kind: 'static'; content: string }
    > => {
      if (filter.type === 'date-range') return []
      if (filter.source === 'distinct') {
        return [{ kind: 'distinct' as const, filterVariableName: filter.variableName }]
      }
      return [
        {
          kind: 'static' as const,
          content: `  const ${filter.variableName}SelectOptions = ${formatSelectOptions(filter.options)}`
        }
      ]
    }
  )

  const clearFilterObject = resolvedFilters
    .flatMap((filter) =>
      filter.type === 'date-range'
        ? [`        '${filter.key}From': null`, `        '${filter.key}To': null`]
        : [`        '${filter.key}': null`]
    )
    .join(',\n')

  const activeFilterCountExpr = `[search${resolvedFilters.map((filter) => (filter.type === 'date-range' ? `, (${filter.fromVariableName} || ${filter.toVariableName})` : `, ${filter.variableName}`)).join('')}].filter(Boolean).length`

  const filterConfigEntries = hasFilters
    ? resolvedFilters
        .map((filter) => {
          if (filter.type === 'date-range') {
            return `    { key: '${filter.key}', label: '${filter.label}', type: 'date-range' as const, from: ${filter.fromVariableName}, to: ${filter.toVariableName} }`
          }
          return `    { key: '${filter.key}', label: '${filter.label}', type: 'select' as const, value: ${filter.variableName}, options: ${filter.variableName}SelectOptions }`
        })
        .join(',\n')
    : ''

  const filterPropsStr = hasFilters
    ? resolvedFilters
        .flatMap((filter) =>
          filter.type === 'date-range'
            ? [
                `${filter.fromVariableName}={${filter.fromVariableName}}`,
                `${filter.toVariableName}={${filter.toVariableName}}`
              ]
            : [`${filter.variableName}={${filter.variableName}}`]
        )
        .join(' ')
    : ''
  const content = renderPageContentFile({
    lucideIcons: lucideIcons.join(', '),
    hasCreate,
    hasFilters,
    hasDelete,
    devModeIntegrationButtonExportName: devModeIntegration.buttonExportName,
    devModeIntegrationButtonImportPath: devModeIntegration.buttonImportPath,
    hookImportNames,
    schemaName: schema.name,
    singular,
    Singular,
    actionImportPath,
    Plural,
    pluralKebab: toKebabCase(plural),
    resolvedFiltersDate,
    resolvedFiltersDateDate,
    resolvedFiltersDistinct,
    filterOptions,
    pluralText: plural,
    escapedSingularNoun: escapeTemplateText(displayNouns.singular),
    escapedPluralNoun: escapeTemplateText(displayNouns.plural),
    deleteFailureMessage: quoteLiteral(`Failed to delete the selected ${displayNouns.plural}`),
    deleteFailureRetryMessage: quoteLiteral(
      `Failed to delete the selected ${displayNouns.plural}. Try again.`
    ),
    plural,
    camelPlural,
    labelExpr,
    adminRoutePath,
    singularizeLabel: singularizeLabel(schema.label),
    clearFilterObject,
    activeFilterCountExpr,
    filterConfigEntries,
    schemaLabel: schema.label,
    filterPropsStr
  })

  return {
    files: [
      createGeneratedFile(`${pagesDir}/${schema.name}/${fileName}`, content),
      ...devModeIntegration.files
    ]
  }
}
